import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import axios from "axios";

import { CONTROLLER_URL, SCHEDULER_LABELS } from "./constants";
import { getHeaders } from "./utils";
import "./scheduler-modal.css";

// Server-side schedulers first, in the order SCHEDULER_LABELS lists them, then
// anything the server reports that this build has never heard of.
function orderKeys(status) {
  const known = Object.keys(SCHEDULER_LABELS).filter((key) => key in status);
  const unknown = Object.keys(status).filter((key) => !(key in SCHEDULER_LABELS));
  return [...known, ...unknown];
}

function readEntry(value) {
  if (value !== null && typeof value === "object") {
    return { enabled: value.enabled === true, session: value };
  }
  return { enabled: value, session: null };
}

function describe(error) {
  const details = error.response?.data?.error || error.response?.data || error.message;
  return typeof details === "string" ? details : JSON.stringify(details, null, 2);
}

function SessionLine({ session }) {
  return (
    <span
      className="scheduler-session"
      title="Whether Chromium has this scheduler's layout open, and whether that page is connected to TradingView"
    >
      <span className={session.layoutOpen ? "on" : "off"}>
        {session.layoutOpen ? "layout open" : "layout closed"}
      </span>
      <span className="sep"> · </span>
      <span className={session.connected ? "on" : "off"}>
        {session.connected ? "connected" : "disconnected"}
      </span>
    </span>
  );
}

/**
 * The single place every server side background loop is switched from.
 *
 * The whole panel is one GET and one POST against /controller: the server owns
 * the list of schedulers, so this renders whatever it is handed rather than a
 * hard-coded set of switches. Browser-local preferences -- the SSE subscription
 * and the home layout -- stay in the settings dropdown, because nothing here
 * would carry them to any other client.
 */
export default function SchedulerModal({ show, onHide }) {
  const [status, setStatus] = useState(null);
  const [unavailable, setUnavailable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Which switch is mid-flight, so only that row shows a spinner.
  const [pending, setPending] = useState(null);

  const applyResponse = (data) => {
    setStatus(data.status || {});
    setUnavailable(data.unavailable || []);
  };

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios({
        method: "get",
        url: CONTROLLER_URL,
        headers: getHeaders(),
        withCredentials: true,
      });
      applyResponse(response.data);
    } catch (err) {
      console.error("Error fetching scheduler status:", err);
      setError(describe(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      loadStatus();
    }
  }, [show, loadStatus]);

  const toggle = async (key) => {
    setPending(key);
    setError(null);
    try {
      const response = await axios({
        method: "post",
        url: CONTROLLER_URL,
        headers: {
          ...getHeaders(),
          "Content-Type": "application/json",
        },
        withCredentials: true,
        data: { [key]: !readEntry(status[key]).enabled },
      });
      // The POST answers with the full picture, so no follow-up read.
      applyResponse(response.data);
    } catch (err) {
      console.error(`Error toggling ${key}:`, err);
      setError(describe(err));
    } finally {
      setPending(null);
    }
  };

  const keys = status ? orderKeys(status) : [];

  return (
    <Modal show={show} onHide={onHide} data-bs-theme="dark" className="scheduler-modal">
      <Modal.Header closeButton className="bg-dark border-secondary">
        <Modal.Title className="text-light">Manage Scheduler</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-light">
        {error && (
          <Alert variant="danger" className="scheduler-error">
            {error}
          </Alert>
        )}

        {loading && !status ? (
          <div className="scheduler-loading">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading…
          </div>
        ) : (
          <>
            {keys.map((key) => {
              const { enabled, session } = readEntry(status[key]);
              // null means the server could not read that toggle's backing
              // store. Drawing it as off would be a lie, and flipping a switch
              // whose current value is unknown is how you turn something on
              // that was meant to stay off, so the row is inert until a reload
              // gets a real answer.
              const unknown = unavailable.includes(key) || enabled === null;
              return (
                <div className="scheduler-item" key={key}>
                  <div
                    className={`status-indicator ${
                      unknown ? "unknown" : enabled ? "enabled" : "disabled"
                    }`}
                  />
                  <div className="scheduler-text">
                    <span className="scheduler-label">{SCHEDULER_LABELS[key] || key}</span>
                    {session && <SessionLine session={session} />}
                  </div>
                  {pending === key ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <Form.Check
                      type="switch"
                      checked={enabled === true}
                      disabled={unknown || pending !== null}
                      onChange={() => toggle(key)}
                    />
                  )}
                </div>
              );
            })}

            {unavailable.length > 0 && (
              <div className="scheduler-note">
                Unreadable on the server right now: {unavailable.join(", ")}. Use Refresh to retry.
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-dark border-secondary">
        <Button variant="secondary" onClick={loadStatus} disabled={loading}>
          Refresh
        </Button>
        <Button variant="primary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
